import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import { getBestFitUnitOfLength } from '@/lib/math'
import { getItem } from '@/plugins/storage'
import { v4 as uuid } from 'uuid'
import { getCenter, getDistance } from 'geolib'
import { VERTEX_LABEL_LENGTH_CUTOFF } from '@/lib/mapbox/line-mode-constants'

class LineViewMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup(lineGeoJson) {
    const lineId = lineGeoJson.properties.id
    const vertexIds = this._generateVertexIds(lineGeoJson)
    const labels = this._generateLabels(lineGeoJson)
    this._state = {
      lineId,
      vertexIds,
      labels,
    }
  }

  /**
   * @inheritDoc
   */
  toDisplayFeatures(geoJson) {
    const line = this._formatLine(geoJson)
    const features = [line]
    if (line.geometry.coordinates.length <= VERTEX_LABEL_LENGTH_CUTOFF) {
      features.push(...this._formatVertices(line))
    }

    if (
      !geoJson.properties.free_hand &&
      line.geometry.coordinates.length <= VERTEX_LABEL_LENGTH_CUTOFF
    ) {
      features.push(...this._state.labels)
    }

    return features
  }

  /**
   * Generates vertex IDs.
   * @param {Object} lineGeoJson - GeoJSON feature.
   * @return {Array<string>} Array of generated vertex IDs.
   * @private
   */
  _generateVertexIds(lineGeoJson) {
    return lineGeoJson.geometry.coordinates.map(() => uuid())
  }

  /**
   * Generate the distance labels for the line
   * @param {Object} lineGeoJson - GeoJSON feature.
   * @return {Array<Object>} Array of label geoJson.
   * @private
   */
  _generateLabels(lineGeoJson) {
    const coordinatesList = lineGeoJson.geometry.coordinates
    const labels = []

    // Using for loop here since we're grabbing multiple items in the array
    // during each iteration
    for (let i = 0; i < coordinatesList.length - 1; i++) {
      const coordinates = [coordinatesList[i], coordinatesList[i + 1]]
      const label = this._generateLabel(coordinates)

      labels.push(label)
    }

    return labels
  }

  /**
   * Generate the GeoJson for a distance label between two points
   * @param {Array<Array>} coordinates - The two coordinates making up
   * the segment of the line the label is for
   * @returns {Object} The GeoJson object for the label
   */
  _generateLabel(coordinates) {
    const unitType = getItem('unitType')
    const distance = getDistance(...coordinates)
    const center = getCenter(coordinates)
    const id = uuid()
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.longitude, center.latitude],
      },
      properties: {
        id,
        title: getBestFitUnitOfLength({
          length: distance,
          from: 'meters',
          unitType: unitType,
        }),
        meta: 'label',
      },
    }
  }

  /**
   * Returns vertex feature at given point location
   * @param {object} point - Point location
   * @param {string} [exceptId] - If passed in, ignore this vertex id
   * @returns {Feature|null} - Vertex feature at location, or null if none found
   * @private
   */
  _getVertexFeatureAtPoint(point, exceptId) {
    return this._getVertexFeaturesAtPoint(point, exceptId)[0] || null
  }

  /**
   * Returns an array of all vertex features at given point location
   * @param {object} point - Point location
   * @param {string} [exceptId] - If passed in, ignore this vertex id
   * @returns {Feature[]} - Array of vertex features at location (may be empty)
   * @private
   */
  _getVertexFeaturesAtPoint(point, exceptId) {
    const features = this.getFeaturesAtPoint(point)
    return features.filter(
      (feature) =>
        feature.properties.meta === 'vertex' &&
        (!exceptId || !exceptId.includes(feature.properties.id))
    )
  }

  /**
   * Formats the line GeoJSON for rendering on the map.
   * @param {Object} lineGeoJson - Line feature GeoJSON.
   * @return {Object} Modified line feature GeoJSON.
   * @private
   */
  _formatLine(lineGeoJson) {
    if (!this._getFeature()) {
      return lineGeoJson
    }
    return lineGeoJson
  }

  /** Formats a list of vertices for rendering on the map.
   * @param {Object} lineGeoJson - The line feature GeoJSON.
   * @return {Array<Object>} List of vertex GeoJSON features to render on the
   * map.
   * @private
   */
  _formatVertices(lineGeoJson) {
    const vertexIds = this._state.vertexIds
    const coordinatesList = lineGeoJson.geometry.coordinates
    const vertices = []

    for (let i = 0; i < vertexIds.length; i++) {
      const id = vertexIds[i]
      const coordinates = coordinatesList[i]

      vertices.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates,
        },
        properties: {
          id,
          meta: 'vertex',
          color: lineGeoJson.properties.color,
        },
      })
    }

    return vertices
  }

  /**
   * Returns the line feature
   * @return {object} Line feature
   */
  _getFeature() {
    const lineId = this._state.lineId
    return this.getFeature(lineId)
  }

  /**
   * Gets the line coordinates
   * @return {array} coordinates
   */
  _getCoordinates() {
    const lineFeature = this._getFeature()
    return lineFeature.geometry.coordinates
  }
}

export default LineViewMode
